import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";

interface HorizontalSliderProps {
  items: React.ReactNode[];
}

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const ItemsWrapper = styled.div`
  display: flex;
  transition: transform 0.3s ease;
  will-change: transform;
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  width: 100%;
  white-space: nowrap;
  &::-webkit-scrollbar {
    display: none; 
  }
`;

const Item = styled.div`
  min-width: 120px;
  margin-right: 16px;
  flex-shrink: 0; 
`;

const HorizontalSlider: React.FC<HorizontalSliderProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(true);
  const [itemWidth, setItemWidth] = useState(136); // Default width before measuring

  useEffect(() => {
    if (containerRef.current) {
      const firstItem = containerRef.current.querySelector(
        "div > div"
      ) as HTMLDivElement;
      if (firstItem) {
        setItemWidth(firstItem.offsetWidth + 16); // Get actual width including margin
      }
    }
  }, [items]);

  const updateArrows = () => {
    const container = containerRef.current;
    if (!container) return;

    setShowPrevArrow(container.scrollLeft > 0);
    setShowNextArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 50
    );
  };

  const slideItems = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (!container) return;

    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - itemWidth * 3
        : container.scrollLeft + itemWidth * 3;
    container.scrollTo({ left: newScrollLeft, behavior: "smooth" });

  };

  useEffect(() => {
    updateArrows();
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => updateArrows();
    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <SliderContainer>
      {showPrevArrow && (
        <IconButton onClick={() => slideItems("left")}>
          <ArrowBack />
        </IconButton>
      )}
      <ScrollContainer ref={containerRef}>
        <ItemsWrapper>
          {items.map((item, index) => (
            <Item key={index}>{item}</Item>
          ))}
        </ItemsWrapper>
      </ScrollContainer>
      {showNextArrow && (
        <IconButton onClick={() => slideItems("right")}>
          <ArrowForward />
        </IconButton>
      )}
    </SliderContainer>
  );
};

export default HorizontalSlider;
